import { Injectable } from '@angular/core';

import { AvailableCalculatorControls } from './calculator-base-form';

import {
	getMaxLoanAmount,
	getMaxLoanTermYears,
	getMinLoanTermYears,
	getMinLoanAmount,
} from 'projects/client/src/app/data/product-ranges';
import { parseFormattedNumber } from 'projects/client/src/app/utils/form';
import {
	CalculationRequestDto,
	CalculationResultResponseDto,
	LoanDetailsDto,
	ProductType,
} from 'shared/openapi/models';

@Injectable({
	providedIn: 'root',
})
export class CalculationMapperService {
	mapCalculationRequestDtoToWebform(dto: CalculationRequestDto) {
		return {
			familyStatus: {
				additionalHouseholdMembers: dto.familyStatus?.additionalHouseholdMembers ?? null,
				atLeast2YearsOfSocialInsurance: dto.familyStatus?.atLeast2YearsOfSocialInsurance ?? null,
				atLeast12weeksPregnant: dto.familyStatus?.atLeast12weeksPregnant ?? false,
				birthDate: dto.familyStatus?.birthDate ? new Date(dto.familyStatus?.birthDate) : null,
				birthDatesOfChildren: dto.familyStatus?.birthDatesOfChildren
					? dto.familyStatus?.birthDatesOfChildren.map((dateStr) => new Date(dateStr))
					: [],
				maritalStatus: dto.familyStatus?.maritalStatus ?? null,
				numberOfAdults: dto.familyStatus?.numberOfAdults ?? 0,
				numberOfChildren: dto.familyStatus?.numberOfChildren ?? 0,
				numberOfExpectedChildren: dto.familyStatus?.numberOfExpectedChildren ?? 0,
				numberOfPensioners: dto.familyStatus?.numberOfPensioners ?? 0,
				partnerBirthDate: dto.familyStatus?.partnerBirthDate
					? new Date(dto.familyStatus?.partnerBirthDate)
					: null,
				previouslyAppliedForCsok: dto.familyStatus?.previouslyAppliedForCsok ?? null,
				hadPreviousSupportRepaymentInPastYear: dto.familyStatus?.hadPreviousSupportRepaymentInPastYear ?? null,
			},
			incomeAndEducation: {
				annuity: dto.incomeAndEducation?.annuity ?? null,
				cafeteria: dto.incomeAndEducation?.cafeteria ?? null,
				creditLimit: dto.incomeAndEducation?.creditLimit ?? null,
				dividendIncome: dto.incomeAndEducation?.dividendIncome ?? null,
				employeeIncome: dto.incomeAndEducation?.employeeIncome ?? null,
				hasLoan: dto.incomeAndEducation?.hasLoan ?? null,
				hasCreditLimit: dto.incomeAndEducation?.hasCreditLimit ?? null,
				familySupport: dto.incomeAndEducation?.familySupport ?? null,
				languageLevel: dto.incomeAndEducation?.languageLevel ?? null,
				highestEducationLevel: dto.incomeAndEducation?.highestEducationLevel ?? null,
				incomeFromPartnership: dto.incomeAndEducation?.incomeFromPartnership ?? null,
				incomeFromSoleProprietorship: dto.incomeAndEducation?.incomeFromSoleProprietorship ?? null,
				incomeFromSoleProprietorshipKataTax:
					dto.incomeAndEducation?.incomeFromSoleProprietorshipKataTax ?? null,
				loanMonthlyFee: dto.incomeAndEducation?.loanMonthlyFee ?? null,
				monthlyNetIncome: dto.incomeAndEducation?.monthlyNetIncome ?? null,
				partnerAnnuity: dto.incomeAndEducation?.partnerAnnuity ?? null,
				partnerCafeteria: dto.incomeAndEducation?.partnerCafeteria ?? null,
				partnerDividendIncome: dto.incomeAndEducation?.partnerDividendIncome ?? null,
				partnerEmployeeIncome: dto.incomeAndEducation?.partnerEmployeeIncome ?? null,
				partnerFamilySupport: dto.incomeAndEducation?.partnerFamilySupport ?? null,
				partnerHasLoan: dto.incomeAndEducation?.partnerHasLoan ?? null,
				partnerHasCreditLimit: dto.incomeAndEducation?.partnerHasCreditLimit ?? null,
				partnerCreditLimit: dto.incomeAndEducation?.partnerCreditLimit ?? null,
				partnerLanguageLevel: dto.incomeAndEducation?.partnerLanguageLevel ?? null,
				partnerHighestEducationLevel: dto.incomeAndEducation?.partnerHighestEducationLevel ?? null,
				partnerIncomeFromPartnership: dto.incomeAndEducation?.partnerIncomeFromPartnership ?? null,
				partnerIncomeFromSoleProprietorship:
					dto.incomeAndEducation?.partnerIncomeFromSoleProprietorship ?? null,
				partnerIncomeFromSoleProprietorshipKataTax:
					dto.incomeAndEducation?.partnerIncomeFromSoleProprietorshipKataTax ?? null,
				partnerLoanMonthlyFee: dto.incomeAndEducation?.partnerLoanMonthlyFee ?? null,
				partnerNetMonthlyIncome: dto.incomeAndEducation?.partnerNetMonthlyIncome ?? null,
				partnerPensionIncome: dto.incomeAndEducation?.partnerPensionIncome ?? null,
				partnerRentalIncome: dto.incomeAndEducation?.partnerRentalIncome ?? null,
				pensionIncome: dto.incomeAndEducation?.pensionIncome ?? null,
				rentalIncome: dto.incomeAndEducation?.rentalIncome ?? null,
				monthlyDepositCommitment: dto.incomeAndEducation?.monthlyDepositCommitment ?? 0,
			},
			loanDetails: {
				babavaroAmount: dto.loanDetails?.babavaroAmount ?? null,
				babavaroTerm:
					dto.loanDetails && dto.loanDetails.babavaroTerm ? dto.loanDetails.babavaroTerm / 12 : null,
				babavaro36MonthsRepaymentSuspension: dto.loanDetails?.babavaro36MonthsRepaymentSuspension ?? null,
				csokPlusAmount: dto.loanDetails?.csokPlusAmount ?? null,
				csokPlusTerm:
					dto.loanDetails && dto.loanDetails.csokPlusTerm ? dto.loanDetails.csokPlusTerm / 12 : null,
				gracePeriod: dto.loanDetails?.gracePeriod ?? null,
				max12MonthsRepaymentSuspension: dto.loanDetails?.max12MonthsRepaymentSuspension ?? null,
				mortgageAmount: dto.loanDetails?.mortgageAmount ?? null,
				mortgageTerm:
					dto.loanDetails && dto.loanDetails.mortgageTerm ? dto.loanDetails.mortgageTerm / 12 : null,
				otthonStartAmount: dto.loanDetails?.otthonStartAmount ?? null,
				otthonStartTerm:
					dto.loanDetails && dto.loanDetails.otthonStartTerm ? dto.loanDetails.otthonStartTerm / 12 : null,
			},
			propertyData: {
				areBothOwnersAtPurchase: dto.propertyData?.areBothOwnersAtPurchase ?? null,
				propertyLocationQuestion: dto.propertyData?.propertyLocationQuestion ?? null,
				propertyLocation: dto.propertyData?.propertyLocation ?? null,
				firstSharedProperty: dto.propertyData?.firstSharedProperty ?? null,
				goalOfApplication: dto.propertyData?.goalOfApplication ?? null,
				purchasePriceOfPlannedProperty: dto.propertyData?.purchasePriceOfPlannedProperty ?? null,
				isPurchasePriceOfPlannedPropertyKnown: dto.propertyData?.isPurchasePriceOfPlannedPropertyKnown ?? null,
				isPropertySizeGreaterThanSpecifiedSize:
					dto.propertyData?.isPropertySizeGreaterThanSpecifiedSize ?? null,
				isSquareMeterPriceWithinLimit: dto.propertyData?.isSquareMeterPriceWithinLimit ?? null,
				hadSignificantResidentialOwnership: dto.propertyData?.hadSignificantResidentialOwnership ?? null,
			},
		};
	}

	mapCalculationWebformToDto(form: AvailableCalculatorControls, productTypes: ProductType[]): CalculationRequestDto {
		if (!productTypes.length) {
			throw new Error('Product types are not set');
		}
		const dto: CalculationRequestDto = {
			productTypes,
			familyStatus: {},
			incomeAndEducation: {},
			loanDetails: {},
			propertyData: {},
		};
		const formValues = form.getRawValue();

		const birthDatesOfChildren =
			formValues.familyStatus?.birthDatesOfChildren?.map(
				(birthDate) => this.convertToIsoDate(birthDate) as string
			) ?? undefined;

		dto.familyStatus = {
			additionalHouseholdMembers: formValues.familyStatus?.additionalHouseholdMembers ?? undefined,
			maritalStatus: formValues.familyStatus?.maritalStatus ?? undefined,
			alreadyHaveSubsidisedLoan: formValues.familyStatus?.alreadyHaveSubsidisedLoan ?? undefined,
			atLeast2YearsOfSocialInsurance: formValues.familyStatus?.atLeast2YearsOfSocialInsurance ?? undefined,
			atLeast12weeksPregnant: formValues.familyStatus?.atLeast12weeksPregnant ?? undefined,
			birthDate: this.convertToIsoDate(formValues.familyStatus?.birthDate) ?? undefined,
			birthDatesOfChildren: birthDatesOfChildren ?? undefined,
			childrenBornFromPreviousSubsidisedLoan:
				formValues.familyStatus?.childrenBornFromPreviousSubsidisedLoan ?? undefined,
			csokAndInterestSupportRepaid: formValues.familyStatus?.csokAndInterestSupportRepaid ?? undefined,
			numberOfAdults: formValues.familyStatus?.numberOfAdults ?? undefined,
			numberOfChildren: formValues.familyStatus?.numberOfChildren ?? undefined,
			numberOfExpectedChildren: formValues.familyStatus?.numberOfExpectedChildren ?? undefined,
			numberOfPensioners: formValues.familyStatus?.numberOfPensioners ?? undefined,
			partnerBirthDate: this.convertToIsoDate(formValues.familyStatus?.partnerBirthDate) ?? undefined,
			previouslyAppliedForCsok: formValues.familyStatus?.previouslyAppliedForCsok ?? undefined,
			hadPreviousSupportRepaymentInPastYear:
				formValues.familyStatus?.hadPreviousSupportRepaymentInPastYear ?? undefined,
		};

		dto.incomeAndEducation = {
			annuity: this.parseFormattedNumber(formValues.incomeAndEducation?.annuity),
			cafeteria: this.parseFormattedNumber(formValues.incomeAndEducation?.cafeteria),
			hasCreditLimit: formValues.incomeAndEducation?.hasCreditLimit ?? undefined,
			hasLoan: formValues.incomeAndEducation?.hasLoan ?? undefined,
			creditLimit: this.parseFormattedNumber(formValues.incomeAndEducation?.creditLimit),
			dividendIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.dividendIncome),
			employeeIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.employeeIncome),
			familySupport: this.parseFormattedNumber(formValues.incomeAndEducation?.familySupport),
			languageLevel: formValues.incomeAndEducation?.languageLevel ?? undefined,
			highestEducationLevel: formValues.incomeAndEducation?.highestEducationLevel ?? undefined,
			incomeFromPartnership: this.parseFormattedNumber(formValues.incomeAndEducation?.incomeFromPartnership),
			incomeFromSoleProprietorship: this.parseFormattedNumber(
				formValues.incomeAndEducation?.incomeFromSoleProprietorship
			),
			incomeFromSoleProprietorshipKataTax: this.parseFormattedNumber(
				formValues.incomeAndEducation?.incomeFromSoleProprietorshipKataTax
			),
			loanMonthlyFee: this.parseFormattedNumber(formValues.incomeAndEducation?.loanMonthlyFee),
			monthlyNetIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.monthlyNetIncome),
			partnerAnnuity: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerAnnuity),
			partnerCafeteria: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerCafeteria),
			partnerDividendIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerDividendIncome),
			partnerEmployeeIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerEmployeeIncome),
			partnerFamilySupport: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerFamilySupport),
			partnerHasCreditLimit: formValues.incomeAndEducation?.partnerHasCreditLimit ?? undefined,
			partnerCreditLimit: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerCreditLimit),
			partnerLanguageLevel: formValues.incomeAndEducation?.partnerLanguageLevel ?? undefined,
			partnerHighestEducationLevel: formValues.incomeAndEducation?.partnerHighestEducationLevel ?? undefined,
			partnerIncomeFromPartnership: this.parseFormattedNumber(
				formValues.incomeAndEducation?.partnerIncomeFromPartnership
			),
			partnerIncomeFromSoleProprietorship: this.parseFormattedNumber(
				formValues.incomeAndEducation?.partnerIncomeFromSoleProprietorship
			),
			partnerIncomeFromSoleProprietorshipKataTax: this.parseFormattedNumber(
				formValues.incomeAndEducation?.partnerIncomeFromSoleProprietorshipKataTax
			),
			partnerHasLoan: formValues.incomeAndEducation?.partnerHasLoan ?? undefined,
			partnerLoanMonthlyFee: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerLoanMonthlyFee),
			partnerNetMonthlyIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerNetMonthlyIncome),
			partnerPensionIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerPensionIncome),
			partnerRentalIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.partnerRentalIncome),
			pensionIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.pensionIncome),
			rentalIncome: this.parseFormattedNumber(formValues.incomeAndEducation?.rentalIncome),
			monthlyDepositCommitment: this.parseFormattedNumber(
				formValues.incomeAndEducation?.monthlyDepositCommitment
			),
		};

		dto.loanDetails = {
			babavaroAmount: this.getLoanAmount(formValues.loanDetails?.babavaroAmount) ?? undefined,
			babavaroTerm:
				formValues.loanDetails && formValues.loanDetails.babavaroTerm
					? formValues.loanDetails.babavaroTerm * 12
					: undefined,
			babavaro36MonthsRepaymentSuspension:
				formValues.loanDetails?.babavaro36MonthsRepaymentSuspension ?? undefined,
			csokPlusAmount: this.getLoanAmount(formValues.loanDetails?.csokPlusAmount) ?? undefined,
			csokPlusTerm:
				formValues.loanDetails && formValues.loanDetails.csokPlusTerm
					? formValues.loanDetails.csokPlusTerm * 12
					: undefined,
			gracePeriod: formValues.loanDetails?.gracePeriod ?? false,
			max12MonthsRepaymentSuspension: formValues.loanDetails?.max12MonthsRepaymentSuspension ?? false,
			mortgageAmount: this.getLoanAmount(formValues.loanDetails?.mortgageAmount) ?? undefined,
			mortgageTerm:
				formValues.loanDetails && formValues.loanDetails.mortgageTerm
					? formValues.loanDetails.mortgageTerm * 12
					: undefined,
			otthonStartAmount: this.getLoanAmount(formValues.loanDetails?.otthonStartAmount) ?? undefined,
			otthonStartTerm:
				formValues.loanDetails && formValues.loanDetails.otthonStartTerm
					? formValues.loanDetails.otthonStartTerm * 12
					: undefined,
		};

		dto.propertyData = {
			areBothOwnersAtPurchase: formValues.propertyData?.areBothOwnersAtPurchase ?? undefined,
			propertyLocation: formValues.propertyData?.propertyLocation ?? undefined,
			propertyLocationQuestion: formValues.propertyData?.propertyLocationQuestion ?? undefined,
			firstSharedProperty: formValues.propertyData?.firstSharedProperty ?? undefined,
			goalOfApplication: formValues.propertyData?.goalOfApplication ?? undefined,
			isPropertySizeGreaterThanSpecifiedSize:
				formValues.propertyData?.isPropertySizeGreaterThanSpecifiedSize ?? undefined,
			isPurchasePriceOfPlannedPropertyKnown:
				formValues.propertyData?.isPurchasePriceOfPlannedPropertyKnown ?? undefined,
			purchasePriceOfPlannedProperty: this.parseFormattedNumber(
				formValues.propertyData?.purchasePriceOfPlannedProperty
			),
			isSquareMeterPriceWithinLimit: formValues.propertyData?.isSquareMeterPriceWithinLimit ?? undefined,
			hadSignificantResidentialOwnership:
				formValues.propertyData?.hadSignificantResidentialOwnership ?? undefined,
		};

		return dto;
	}

	patchCalculationRequestLoanAmount(dto: CalculationRequestDto) {
		const request = structuredClone(dto);
		request.loanDetails!.babavaroAmount = this.getLoanAmount(request.loanDetails?.babavaroAmount);
		request.loanDetails!.csokPlusAmount = this.getLoanAmount(request.loanDetails?.csokPlusAmount);
		request.loanDetails!.mortgageAmount = this.getLoanAmount(request.loanDetails?.mortgageAmount);
		request.loanDetails!.otthonStartAmount = this.getLoanAmount(request.loanDetails?.otthonStartAmount);
		return request;
	}

	getLoanAmount(amount: number | null | undefined) {
		if (amount === null || amount === undefined) {
			return undefined;
		}

		const isAmountAlreadyInMillions = amount >= 1_000_000;

		return isAmountAlreadyInMillions ? amount : amount * 1_000_000;
	}

	patchFormWithCalculationResultCriteria(
		form: AvailableCalculatorControls,
		productTypes: ProductType[],
		result: CalculationResultResponseDto
	) {
		const formValues = form.getRawValue();

		const babaValues: Partial<LoanDetailsDto> = {};
		const csokValues: Partial<LoanDetailsDto> = {};
		const jelzalogValues: Partial<LoanDetailsDto> = {};
		const otthonStartValues: Partial<LoanDetailsDto> = {};

		if (productTypes.includes(ProductType.Babavaro)) {
			const absMaxAmount = getMaxLoanAmount(ProductType.Babavaro) / 1_000_000;
			const formAmount = this.checkAndReduceAmount(formValues.loanDetails?.babavaroAmount) ?? absMaxAmount;
			const resultAmount = result.maxAmountOfLoanBabavaro
				? result.maxAmountOfLoanBabavaro / 1_000_000
				: undefined;
			const maxAmount = Math.min(resultAmount ?? formAmount, formAmount);
			const absMaxTerm = getMaxLoanTermYears(ProductType.Babavaro);
			const formTerm = formValues.loanDetails?.babavaroTerm ?? absMaxTerm;
			const minTerm = Math.ceil(
				result.minLoanTermBabavaro ? result.minLoanTermBabavaro / 12 : getMinLoanTermYears(ProductType.Babavaro)
			);
			const babavaroTerm = formTerm < minTerm ? minTerm : formTerm;

			babaValues.babavaroAmount = Math.max(
				Math.floor(maxAmount * 100) / 100,
				getMinLoanAmount(ProductType.Babavaro) / 1_000_000
			);
			babaValues.babavaroTerm = babavaroTerm;
		}

		if (productTypes.includes(ProductType.CsokPlusz)) {
			const absMaxAmount = getMaxLoanAmount(ProductType.CsokPlusz) / 1_000_000;
			const formAmount = this.checkAndReduceAmount(formValues.loanDetails?.csokPlusAmount) ?? absMaxAmount;
			const resultAmount = result.maxAmountOfLoanCsokPlus
				? result.maxAmountOfLoanCsokPlus / 1_000_000
				: undefined;
			const maxAmount = Math.min(resultAmount ?? formAmount, formAmount);
			const absMaxTerm = getMaxLoanTermYears(ProductType.CsokPlusz);
			const formTerm = formValues.loanDetails?.csokPlusTerm ?? absMaxTerm;
			const minTerm = Math.ceil(
				result.minLoanTermCsokPlus
					? result.minLoanTermCsokPlus / 12
					: getMinLoanTermYears(ProductType.CsokPlusz)
			);
			const csokPluszTerm = formTerm < minTerm ? minTerm : formTerm;

			csokValues.csokPlusAmount = Math.max(
				Math.floor(maxAmount * 100) / 100,
				getMinLoanAmount(ProductType.CsokPlusz) / 1_000_000
			);
			csokValues.csokPlusTerm = csokPluszTerm;
		}

		if (productTypes.includes(ProductType.Jelzalog)) {
			const absMaxAmount = getMaxLoanAmount(ProductType.Jelzalog) / 1_000_000;
			const formAmount = this.checkAndReduceAmount(formValues.loanDetails?.mortgageAmount) ?? absMaxAmount;
			const resultAmount = result.maxAmountOfLoanJelzalog
				? result.maxAmountOfLoanJelzalog / 1_000_000
				: undefined;
			const maxAmount = Math.min(resultAmount ?? formAmount, formAmount);
			const absMaxTerm = getMaxLoanTermYears(ProductType.Jelzalog);
			const formTerm = formValues.loanDetails?.mortgageTerm ?? absMaxTerm;
			const minTerm = Math.ceil(
				result.minLoanTermJelzalog ? result.minLoanTermJelzalog / 12 : getMinLoanTermYears(ProductType.Jelzalog)
			);
			const mortgageTerm = formTerm < minTerm ? minTerm : formTerm;

			jelzalogValues.mortgageAmount = Math.max(
				Math.floor(maxAmount * 100) / 100,
				getMinLoanAmount(ProductType.Jelzalog) / 1_000_000
			);
			jelzalogValues.mortgageTerm = mortgageTerm;
		}

		if (productTypes.includes(ProductType.OtthonStart)) {
			const absMaxAmount = getMaxLoanAmount(ProductType.OtthonStart) / 1_000_000;
			const formAmount = this.checkAndReduceAmount(formValues.loanDetails?.otthonStartAmount) ?? absMaxAmount;
			const resultAmount = result.maxAmountOfLoanOtthonStart
				? result.maxAmountOfLoanOtthonStart / 1_000_000
				: undefined;
			const maxAmount = Math.min(resultAmount ?? formAmount, formAmount);
			const absMaxTerm = getMaxLoanTermYears(ProductType.OtthonStart);
			const formTerm = formValues.loanDetails?.otthonStartTerm ?? absMaxTerm;
			const minTerm = Math.ceil(
				result.minLoanTermOtthonStart
					? result.minLoanTermOtthonStart / 12
					: getMinLoanTermYears(ProductType.OtthonStart)
			);
			const otthonStartTerm = formTerm < minTerm ? minTerm : formTerm;

			otthonStartValues.otthonStartAmount = Math.max(
				Math.floor(maxAmount * 100) / 100,
				getMinLoanAmount(ProductType.OtthonStart) / 1_000_000
			);
			otthonStartValues.otthonStartTerm = otthonStartTerm;
		}

		form.patchValue({
			...formValues,
			loanDetails: {
				...formValues.loanDetails,
				...babaValues,
				...csokValues,
				...jelzalogValues,
				...otthonStartValues,
			},
			propertyData: {
				...formValues.propertyData,
				purchasePriceOfPlannedProperty: result.purchasePriceOfPlannedProperty ?? 0,
			},
		});

		return form;
	}

	getMappedLoanDetails(request: CalculationRequestDto): CalculationRequestDto['loanDetails'] {
		return {
			babavaroAmount: request.loanDetails?.babavaroAmount ?? 0,
			babavaroTerm: (request.loanDetails?.babavaroTerm ?? 0) * 12,
			csokPlusAmount: request.loanDetails?.csokPlusAmount ?? 0,
			csokPlusTerm: (request.loanDetails?.csokPlusTerm ?? 0) * 12,
			gracePeriod: request.loanDetails?.gracePeriod,
			max12MonthsRepaymentSuspension: request.loanDetails?.max12MonthsRepaymentSuspension,
			mortgageAmount: request.loanDetails?.mortgageAmount ?? 0,
			mortgageTerm: (request.loanDetails?.mortgageTerm ?? 0) * 12,
			otthonStartAmount: request.loanDetails?.otthonStartAmount ?? 0,
			otthonStartTerm: (request.loanDetails?.otthonStartTerm ?? 0) * 12,
		};
	}

	convertToIsoDate(date: string | Date | null | undefined) {
		if (date instanceof Date) {
			return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
		}
		return date;
	}

	private parseFormattedNumber(value?: number | null) {
		const formatted = parseFormattedNumber(value ?? '');
		return isNaN(formatted) ? undefined : formatted;
	}

	private checkAndReduceAmount(amount: number | null | undefined) {
		if (!amount) return amount;

		return amount > 1_000_000 ? amount / 1_000_000 : amount;
	}
}
